import { Labeled } from '@components/form/Labeled'
import {
  formatIdNumber,
  ID_PATTERN,
  PASSPORT_PATTERN
} from '@features/users/idFormats'
import {
  CountryField,
  DegreeField,
  NationalityField
} from '@features/users/components/UsersFormSelects'
import { useCountries } from '@services/common/useCountries'
import {
  type ContractMinor,
  useContractMinors,
  useSaveContractMinors
} from '@services/contracts/minors'
import { format, parseISO } from 'date-fns'
import { Baby, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'

const isoDate = (d: Date) => format(d, 'yyyy-MM-dd')

// A minor is under 18: born after this day 18 years ago, and not in the future.
function birthDateRange() {
  const today = new Date()
  const min = new Date(today)
  min.setFullYear(today.getFullYear() - 18)
  min.setDate(min.getDate() + 1)
  return { min: isoDate(min), max: isoDate(today) }
}

const EMPTY: ContractMinor = {
  degree: null,
  first_name: '',
  last_name: '',
  birth_date: '',
  id_number: '',
  passport_number: '',
  street: '',
  address_number: '',
  address_orientation_number: '',
  zip: '',
  city: '',
  country: '',
  nationality: '',
  representative: ''
}

type Props = {
  offerId: string
  /** Called after the list was saved, to refresh the contract preview. */
  onSaved: () => void
}

/**
 * Minors named in the tenant contract (e.g. a child who needs the contract
 * for prechodný pobyt). Saved on their own, independently of the points.
 */
export function ContractMinorsSection({ offerId, onSaved }: Props) {
  const { t } = useTranslation('clauses')
  const { confirm, dialog } = useConfirm()
  const minors = useContractMinors(offerId)
  const save = useSaveContractMinors(offerId)
  // Index of the minor being edited, 'new' for a new one.
  const [editing, setEditing] = useState<number | 'new' | null>(null)

  const list = minors.data?.minors ?? []
  const signers = minors.data?.signers ?? []
  const signerName = (id: string) =>
    signers.find((s) => s.id === id)?.name ?? '—'

  const persist = async (next: ContractMinor[]) => {
    try {
      await save.mutateAsync(next)
    } catch {
      return false
    }
    setEditing(null)
    onSaved()
    return true
  }

  const remove = async (index: number) => {
    const minor = list[index]
    if (
      !minor ||
      !(await confirm({
        title: t('minors.remove_title'),
        description: t('minors.remove_description', {
          name: `${minor.first_name} ${minor.last_name}`
        }),
        confirmText: t('remove'),
        cancelText: t('cancel'),
        destructive: true
      }))
    )
      return
    await persist(list.filter((_, i) => i !== index))
  }

  return (
    <section className="mb-6 space-y-2 rounded-2xl border border-ink/10 p-4">
      {dialog}
      <h4 className="m-0 flex items-center gap-2 font-display text-sm font-bold text-ink">
        <Baby className="h-4 w-4 text-primary" />
        {t('minors.title')}
      </h4>
      <p className="m-0 text-xs text-muted-foreground">{t('minors.hint')}</p>

      {minors.isLoading && (
        <div className="h-12 animate-pulse rounded-xl bg-ink/[0.04]" />
      )}

      {list.map((minor, index) =>
        editing === index ? (
          <MinorForm
            key={minor.id ?? index}
            initial={minor}
            signers={signers}
            saving={save.isPending}
            onCancel={() => setEditing(null)}
            onSubmit={(values) =>
              persist(list.map((m, i) => (i === index ? values : m)))
            }
          />
        ) : (
          <div
            key={minor.id ?? index}
            className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-ink/[0.03] px-3 py-2"
          >
            <div className="text-sm text-ink">
              <span className="font-semibold">
                {minor.first_name} {minor.last_name}
              </span>
              <span className="text-muted-foreground">
                {' · '}
                {format(parseISO(minor.birth_date), 'd. M. yyyy')}
                {' · '}
                {t('minors.represented_by', {
                  name: signerName(minor.representative)
                })}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghostDark"
                disabled={editing !== null || save.isPending}
                onClick={() => setEditing(index)}
              >
                <Pencil className="h-3.5 w-3.5" />
                {t('edit')}
              </Button>
              <Button
                size="sm"
                variant="ghostDark"
                disabled={editing !== null || save.isPending}
                onClick={() => void remove(index)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t('remove')}
              </Button>
            </div>
          </div>
        )
      )}

      {editing === 'new' ? (
        <MinorForm
          initial={EMPTY}
          signers={signers}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSubmit={(values) => persist([...list, values])}
        />
      ) : (
        <Button
          size="sm"
          variant="ghostDark"
          disabled={editing !== null || minors.isLoading}
          onClick={() => setEditing('new')}
        >
          <Plus className="h-3.5 w-3.5" />
          {t('minors.add')}
        </Button>
      )}
    </section>
  )
}

function MinorForm({
  initial,
  signers,
  saving,
  onCancel,
  onSubmit
}: {
  initial: ContractMinor
  signers: { id: string; name: string; can_represent: boolean }[]
  saving: boolean
  onCancel: () => void
  /** Resolves to false when saving failed (the form stays open). */
  onSubmit: (values: ContractMinor) => Promise<boolean>
}) {
  const { t } = useTranslation('clauses')
  const { data: countries } = useCountries({ search: '' })
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<ContractMinor>({ mode: 'onTouched', defaultValues: initial })

  const countryId = watch('country')
  const countryName = countries
    ?.find((c) => String(c.id) === countryId)
    ?.name?.toLowerCase()
  // Same rule as the backend: passport for a selected non-Slovak country,
  // rodné číslo otherwise.
  const foreign =
    !!countryId && countryName !== 'slovensko' && countryName !== 'slovakia'
  const range = birthDateRange()
  const required = t('minors.required')
  const invalidCls = (e?: unknown) => (e ? 'border-destructive' : '')

  const submit = handleSubmit(async (values) => {
    await onSubmit({
      ...values,
      // Only the identification that applies is kept.
      id_number: foreign ? '' : values.id_number,
      passport_number: foreign ? values.passport_number : ''
    })
  })

  return (
    <form
      className="space-y-4 rounded-xl border border-primary/30 bg-primary/[0.03] p-4"
      onSubmit={(e) => {
        e.stopPropagation()
        void submit(e)
      }}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[150px_1fr_1fr]">
        <Controller
          control={control}
          name="degree"
          render={({ field }) => (
            <DegreeField
              value={field.value ?? undefined}
              // Clear to null, otherwise RHF falls back to the default value.
              onChange={(v) => field.onChange(v ?? null)}
            />
          )}
        />
        <Labeled
          label={t('minors.first_name')}
          required
          error={errors.first_name?.message}
        >
          <Input
            className={invalidCls(errors.first_name)}
            {...register('first_name', { required })}
          />
        </Labeled>
        <Labeled
          label={t('minors.last_name')}
          required
          error={errors.last_name?.message}
        >
          <Input
            className={invalidCls(errors.last_name)}
            {...register('last_name', { required })}
          />
        </Labeled>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Controller
          control={control}
          name="birth_date"
          rules={{
            required,
            validate: (v) =>
              !v ||
              (v >= range.min && v <= range.max) ||
              t('minors.not_minor')
          }}
          render={({ field }) => (
            <Labeled
              label={t('minors.birth_date')}
              required
              error={errors.birth_date?.message}
            >
              <DatePicker
                min={range.min}
                max={range.max}
                value={field.value || undefined}
                onChange={(v) => field.onChange(v ?? '')}
                invalid={!!errors.birth_date}
              />
            </Labeled>
          )}
        />

        <Controller
          control={control}
          name="representative"
          rules={{ required }}
          render={({ field }) => (
            <Labeled
              label={t('minors.representative')}
              required
              hint={t('minors.representative_hint')}
              error={errors.representative?.message}
            >
              <Combobox
                searchable={false}
                value={field.value || undefined}
                onChange={(v) => field.onChange(v ?? '')}
                options={signers
                  .filter((s) => s.can_represent)
                  .map((s) => ({ label: s.name, value: s.id }))}
                invalid={!!errors.representative}
                placeholder={t('minors.representative_placeholder')}
              />
            </Labeled>
          )}
        />

        <Controller
          control={control}
          name="nationality"
          rules={{ required }}
          render={({ field }) => (
            <NationalityField
              required
              value={field.value || undefined}
              onChange={(v) => field.onChange(v ?? '')}
              error={errors.nationality?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="country"
          rules={{ required }}
          render={({ field }) => (
            <CountryField
              required
              value={field.value || undefined}
              onChange={(v) => field.onChange(v ?? '')}
              error={errors.country?.message}
            />
          )}
        />

        {foreign ? (
          <Controller
            key="passport_number"
            control={control}
            name="passport_number"
            rules={{
              required,
              pattern: {
                value: PASSPORT_PATTERN,
                message: t('minors.passport_format')
              }
            }}
            render={({ field }) => (
              <Labeled
                label={t('minors.passport_number')}
                required
                error={errors.passport_number?.message}
              >
                <Input
                  placeholder="AB0123456"
                  className={invalidCls(errors.passport_number)}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value.toUpperCase())
                  }
                  onBlur={field.onBlur}
                />
              </Labeled>
            )}
          />
        ) : (
          <Controller
            key="id_number"
            control={control}
            name="id_number"
            rules={{
              required,
              pattern: { value: ID_PATTERN, message: t('minors.id_format') }
            }}
            render={({ field }) => (
              <Labeled
                label={t('minors.id_number')}
                required
                error={errors.id_number?.message}
              >
                <Input
                  placeholder="123456/1234"
                  maxLength={11}
                  className={invalidCls(errors.id_number)}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(formatIdNumber(e.target.value))
                  }
                  onBlur={field.onBlur}
                />
              </Labeled>
            )}
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr_1fr]">
        <Labeled
          label={t('minors.street')}
          required
          error={errors.street?.message}
        >
          <Input
            className={invalidCls(errors.street)}
            {...register('street', { required })}
          />
        </Labeled>
        <Labeled label={t('minors.address_number')}>
          <Input {...register('address_number')} />
        </Labeled>
        <Labeled
          label={t('minors.address_orientation_number')}
          required
          error={errors.address_orientation_number?.message}
        >
          <Input
            className={invalidCls(errors.address_orientation_number)}
            {...register('address_orientation_number', { required })}
          />
        </Labeled>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_2fr]">
        <Labeled label={t('minors.zip')} required error={errors.zip?.message}>
          <Input
            className={invalidCls(errors.zip)}
            {...register('zip', { required })}
          />
        </Labeled>
        <Labeled label={t('minors.city')} required error={errors.city?.message}>
          <Input
            className={invalidCls(errors.city)}
            {...register('city', { required })}
          />
        </Labeled>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghostDark"
          onClick={onCancel}
          disabled={saving}
        >
          {t('cancel')}
        </Button>
        <Button type="submit" disabled={saving}>
          {t('minors.save')}
        </Button>
      </div>
    </form>
  )
}
